import { AnnouncementBanner } from '@ui/components/Layout/AnnouncementBanner';
import { useBrand } from '@ui/config/BrandProvider';
import {
  isAddressInFairLaunchAllowlist,
  isAddressInPrivacyPoolAllowlist,
} from '@ui/constants/allowlist';
import { isStaEnv } from '@ui/constants/env';
import {
  ANYCOLLATERAL_PROGRAM_LINK,
  BLOG_LINK,
  DISCORD_LINK,
  DUNE_LINK,
  GIT_BOOK_AUDITS,
  GIT_BOOK_LINK,
  TELEGRAM_LINK,
  TERMS_CONDITION,
  X_LINK,
} from '@ui/constants/links';
import { ROUTES } from '@ui/constants/routes';
import { isAddressInOneClickAllowlist } from '@ui/features/OneClickTrading/constants/allowlist';
import { useOneClickTradingModalStore } from '@ui/features/OneClickTrading/store/useOneClickTradingModalStore';
import { useOneClickTradingStore } from '@ui/features/OneClickTrading/store/useOneClickTradingStore';
import { HeaderVipButton } from '@ui/features/Vip/components/HeaderVipButton';
import { NextLinkWithLocale } from '@ui/lib/i18n';
import { Badge } from '@ui/lib/park/ui/badge';
import { Button } from '@ui/lib/park/ui/button';
import { Drawer } from '@ui/lib/park/ui/drawer';
import { Text } from '@ui/lib/park/ui/text';
import { getActiveRoute } from '@ui/utils/getActiveRoute';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Fragment, useMemo } from 'react';
import { css } from 'styled-system/css';
import { Box, BoxProps, Flex, HStack, Stack, styled } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { MoreMenuIcon, MoreMenuIconKind, TopMoreMenu } from './TopMoreMenu';
import { TopTabs, tintedIconClass } from './TopTabs';
import { getRoutesArray } from './utils/getRoutesArray';
import { ConnectWallet } from './WalletConnect';

type TopNavBarProps = BoxProps;

const enableOneClickButtonClass = css({
  position: 'relative',
  color: 'greyWhite.white',
  textShadow: '0 0 12px rgba(255, 255, 255, 0.35)',
  border: '1px solid transparent',
  backgroundImage:
    'linear-gradient(120deg, rgba(14, 15, 14, 0.95), rgba(14, 15, 14, 0.7)), linear-gradient(120deg, var(--colors-primary-primary), #3cffe1, #a855ff, var(--colors-primary-primary))',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  backgroundSize: '100% 100%, 300% 300%',
  animation: 'auroraShift 6s ease infinite',
  boxShadow:
    '0 0 8px rgba(204, 255, 0, 0.35), 0 0 15px rgba(60, 255, 205, 0.25)',
  isolation: 'isolate',
  overflow: 'hidden',
  transition: 'transform 200ms ease, box-shadow 200ms ease',
  minW: 'fit-content',
  _before: {
    content: '""',
    position: 'absolute',
    inset: '2px',
    borderRadius: 'inherit',
    background:
      'radial-gradient(circle at 25% 20%, rgba(204, 255, 0, 0.4), transparent 55%)',
    filter: 'blur(8px)',
    opacity: 0.85,
    animation: 'twinkle 3.5s ease-in-out infinite',
    zIndex: -1,
  },
  _after: {
    content: '""',
    position: 'absolute',
    top: '10%',
    bottom: '10%',
    width: '35%',
    left: '-35%',
    borderRadius: 'inherit',
    background:
      'linear-gradient(120deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0))',
    mixBlendMode: 'screen',
    filter: 'blur(4px)',
    animation: 'glintSweep 2.8s cubic-bezier(0.65, 0, 0.35, 1) infinite',
    zIndex: -1,
  },
  _hover: {
    transform: 'scale(1.01)',
    boxShadow:
      '0 1px 10px rgba(204, 255, 0, 0.5), 0 0 20px rgba(60, 255, 205, 0.35)',
  },
  // _active: {
  //   transform: 'scale(0.98)',
  // },
  // _focusVisible: {
  //   boxShadow:
  //     '0 0 0 2px rgba(204, 255, 0, 0.65), 0 0 40px rgba(60, 255, 205, 0.4)',
  // },
});

const topNavLogoLinkClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
});

const topNavLogoImgClass = css({
  width: { base: '125px', desktop: '96px' },
  height: { base: '22px', desktop: '24px' },
  minW: { base: '125px', desktop: '96px' },
  minH: { base: '22px', desktop: '24px' },
  maxW: { base: '125px', desktop: '96px' },
  flexShrink: 0,
  objectFit: 'contain',
});

const topNavDrawerLogoImgClass = css({
  width: '100px',
  height: '18px',
  minW: '100px',
  minH: '18px',
  maxW: '100px',
  flexShrink: 0,
  objectFit: 'contain',
});

const mobileDrawerLinkClass = css({
  textDecoration: 'none',
  display: 'block',
  width: '100%',
});

const mobileDrawerFooterLinkClass = css({
  color: 'greyWhite.grey',
  textDecoration: 'none',
  _hover: { color: 'greyWhite.white' },
});

type MobileDrawerLinkProps = {
  label: string;
  href: string;
  external?: boolean;
  icon?: MoreMenuIconKind;
};

const MobileDrawerLink = ({
  label,
  href,
  external = false,
  icon,
}: MobileDrawerLinkProps) => {
  const content = (
    <Box px={5} py={4} minH="48px" borderRadius="8px" bg="transparent">
      <HStack gap="8px" alignItems="center">
        {icon && <MoreMenuIcon kind={icon} />}
        <Text textStyle="headings.default" color="greyWhite.lightGrey2">
          {label}
        </Text>
      </HStack>
    </Box>
  );

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={mobileDrawerLinkClass}
      >
        {content}
      </a>
    );
  }

  return (
    <NextLinkWithLocale href={href} className={mobileDrawerLinkClass}>
      {content}
    </NextLinkWithLocale>
  );
};

// Fallback icons for drawer tabs that have no brand `tokenIcon`.
const DRAWER_FALLBACK_ICON: Partial<Record<string, MoreMenuIconKind>> = {
  portfolio: 'portfolio',
  leaderboard: 'leaderboard',
};

/**
 * Reusable TopNavBar component
 *
 * On mobile, the children shows at bottom.
 *
 * On desktop, the children shows beside connect wallet button.
 *
 * TODO: confirm desktop design to see if the children position is correct
 */
export const TopNavBar: React.FC<TopNavBarProps> = ({ children, ...props }) => {
  const { t } = useTranslation();
  const brand = useBrand();
  const router = useRouter();
  const { address, isConnected } = useAccount();
  const isOneClickEnabled = useOneClickTradingStore((s) => s.enabled);
  const hasCheckedOneClickStatus = useOneClickTradingStore(
    (s) => s.hasCheckedStatus,
  );
  const openOneClickTrading = useOneClickTradingModalStore((s) => s.open);
  const isOneClickAllowlisted = isAddressInOneClickAllowlist(address);
  const showPrivacyPoolsMenu = isAddressInPrivacyPoolAllowlist(address);
  const showFairLaunchTab = isAddressInFairLaunchAllowlist(address);
  const mobileDrawerExtraLinks = useMemo(() => {
    const links: MobileDrawerLinkProps[] = [
      {
        label: t('common.Docs', 'Docs'),
        href: GIT_BOOK_LINK,
        external: true,
        icon: 'docs',
      },
      {
        label: t('topMore.duneStats', 'Dune (Stats)'),
        href: DUNE_LINK,
        external: true,
        icon: 'stats',
      },
      {
        label: t('common.Blog', 'Blog'),
        href: BLOG_LINK,
        external: true,
        icon: 'blog',
      },
    ];

    if (showPrivacyPoolsMenu) {
      links.push({
        label: t('common.PrivacyPools', 'Privacy Pools'),
        href: ROUTES.PRIVACY_POOLS,
        icon: 'privacy',
      });
    }

    links.push({
      label: t('topMore.anyCollateralProgram', 'AnyCollateral Program'),
      href: ANYCOLLATERAL_PROGRAM_LINK,
      external: true,
      icon: 'anyCollateral',
    });

    return links;
  }, [showPrivacyPoolsMenu, t]);
  // Mobile drawer uses this list; keep it in sync with the drawer UI.
  // Note: we intentionally do NOT change `getRoutesArray` here to avoid
  // impacting desktop TopTabs / MobileBottomNavBar.
  const { tabs, tabGroups } = useMemo(() => {
    const baseTabs = getRoutesArray(t).filter(
      (item) =>
        brand.navItems.includes(item.key) &&
        (item.key !== 'fairlaunch' || showFairLaunchTab),
    );
    const leaderboardTab = {
      key: 'leaderboard',
      url: ROUTES.LEADERBOARD,
      label: t('common.Leaderboard', 'Leaderboard'),
    };
    const shouldShowLeaderboard = brand.navItems.includes('leaderboard');

    const pick = (key: string) => baseTabs.find((v) => v.key === key);
    const isDefined = <T,>(v: T): v is NonNullable<T> => v != null;

    // 3 categories:
    // 1) Trade + LVUSD + LVMON
    // 2) Earn + Airdrop + Portfolio
    // 3) Competition + Leaderboard
    const groups = [
      [pick('trade'), pick('LVUSD'), pick('LVMON')].filter(isDefined),
      [pick('earn'), pick('airdrop'), pick('portfolio')].filter(isDefined),
      [
        pick('competition'),
        shouldShowLeaderboard ? leaderboardTab : null,
      ].filter(isDefined),
    ].filter((group) => group.length > 0);

    return {
      tabs: groups.flat(),
      tabGroups: groups,
    };
  }, [brand.navItems, showFairLaunchTab, t]);
  const activeTab = useMemo(
    () =>
      getActiveRoute(
        tabs.map((v) => v.url),
        router.pathname,
      ) ?? router.pathname,
    [router.pathname, tabs],
  );

  return (
    <>
      <Box
        position={{
          base: 'sticky',
          desktop: 'fixed',
        }}
        top={{
          base: 0,
          desktop: 0,
        }}
        h={{
          base: 'auto',
          desktop: `layout.topNavBarHeight`,
        }}
        zIndex="banner"
        w="100%"
        bgRepeat="repeat"
        bgSize="500px 500px"
        bgColor="var(--brand-nav-bg)" // dynamic per domain
        borderBottom={{
          base: '1px solid #272b27',
          desktop: '1px solid #272b27',
        }}
        {...props}
      >
        <Flex
          height="full"
          p={{
            base: '10px 8px',
            desktop: '0 14px',
          }}
          zIndex="sticky"
          position="relative"
          flexDir={{
            base: 'column',
            desktop: 'row',
          }}
          gap={{
            base: '0',
            desktop: '16px',
          }}
          justifyContent={{
            base: 'flex-start',
            desktop: 'space-between',
          }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            minW={0}
            h={{ desktop: 'full' }}
          >
            <HStack gap="8px" flexShrink={0}>
              <NextLinkWithLocale
                className={topNavLogoLinkClass}
                href={ROUTES.INDEX}
              >
                <styled.img
                  src={brand.logoSrc}
                  alt="logo icon"
                  loading="eager"
                  className={topNavLogoImgClass}
                />
              </NextLinkWithLocale>
              {/* Testnet badge hidden for now */}
              {false && isStaEnv && (
                <Badge
                  boxShadow="none"
                  size="md"
                  borderRadius="full"
                  px="12px"
                  py="6px"
                  bg="black.lv3"
                  color="primary.primary"
                >
                  {t('common.Testnet')}
                </Badge>
              )}
            </HStack>
            <HStack>
              <HeaderVipButton
                display={{ base: 'inline-flex', desktop: 'none' }}
              />
              <ConnectWallet
                display={{
                  base: 'flex',
                  desktop: 'none',
                }}
              />
              {/* Mobile menu drawer */}
              <Drawer.Root lazyMount unmountOnExit>
                <Drawer.Trigger
                  display={{ base: 'flex', desktop: 'none' }}
                  className={css({
                    px: '5px',
                    py: '5px',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: '#2A2A2A',
                    color: 'greyWhite.lightGrey2',
                    bg: '#1E1E1E',
                  })}
                  aria-label="Open menu"
                >
                  <styled.img
                    src="/icons/drawer.svg"
                    alt="menu icon"
                    loading="eager"
                    width={{ base: '20px' }}
                    height={{ base: '20px' }}
                    p="1px"
                    color="currentColor"
                  />
                </Drawer.Trigger>
                <Drawer.Positioner>
                  <Drawer.Content bg="#1B1B1B" borderBottom="1px solid #494949">
                    <Drawer.Header
                      p={3}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      w="full"
                    >
                      <HStack gap="8px" flexShrink={0}>
                        <NextLinkWithLocale
                          className={topNavLogoLinkClass}
                          href={ROUTES.INDEX}
                        >
                          <styled.img
                            src={brand.logoSrc}
                            alt="logo icon"
                            className={topNavDrawerLogoImgClass}
                          />
                        </NextLinkWithLocale>
                        {isStaEnv && (
                          <Badge
                            boxShadow="none"
                            size="md"
                            borderRadius="full"
                            px="12px"
                            py="6px"
                            bg="black.lv3"
                            color="primary.primary"
                          >
                            {t('common.Testnet')}
                          </Badge>
                        )}
                      </HStack>
                      <Drawer.CloseTrigger />
                    </Drawer.Header>
                    <Drawer.Body p={2}>
                      <Stack gap={1}>
                        {tabGroups.map((group, groupIdx) => {
                          return (
                            <Fragment key={`drawer-group-${groupIdx}`}>
                              {group.map((route) => {
                                const isActive = activeTab === route.url;
                                const { tokenIcon, tintIcon } = route as {
                                  tokenIcon?: string;
                                  tintIcon?: boolean;
                                };
                                const fallbackIcon =
                                  DRAWER_FALLBACK_ICON[route.key];

                                return (
                                  <NextLinkWithLocale
                                    key={route.key}
                                    href={route.url}
                                    className={css({
                                      textDecoration: 'none',
                                      display: 'block',
                                      width: '100%',
                                    })}
                                  >
                                    <Box
                                      px={5}
                                      py={4}
                                      minH="48px"
                                      borderRadius="8px"
                                      bg={isActive ? '#222222' : 'transparent'}
                                    >
                                      <HStack gap="8px" alignItems="center">
                                        {tokenIcon ? (
                                          <styled.img
                                            src={tokenIcon}
                                            width="20px"
                                            height="20px"
                                            className={
                                              tintIcon
                                                ? tintedIconClass
                                                : undefined
                                            }
                                          />
                                        ) : fallbackIcon ? (
                                          <MoreMenuIcon kind={fallbackIcon} />
                                        ) : null}
                                        {typeof route.label === 'string' ? (
                                          <Text
                                            textStyle="headings.default"
                                            color={
                                              isActive
                                                ? 'var(--brand-active-nav)'
                                                : 'greyWhite.lightGrey2'
                                            }
                                          >
                                            {route.label}
                                          </Text>
                                        ) : (
                                          route.label
                                        )}
                                      </HStack>
                                    </Box>
                                  </NextLinkWithLocale>
                                );
                              })}

                              {/* divider between groups */}
                              {groupIdx < tabGroups.length - 1 ? (
                                <Box h="1px" bg="#2A2A2A" mx={5} my={1} />
                              ) : null}
                            </Fragment>
                          );
                        })}
                        {/* divider before More-menu links */}
                        <Box h="1px" bg="#2A2A2A" mx={5} my={1} />
                        {mobileDrawerExtraLinks.map((link) => (
                          <MobileDrawerLink key={link.href} {...link} />
                        ))}
                      </Stack>
                    </Drawer.Body>
                    <Drawer.Footer
                      p={4}
                      position="sticky"
                      bottom={0}
                      bg="#1B1B1B"
                      borderTop="1px solid #2A2A2A"
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        w="full"
                        gap="12px"
                      >
                        <HStack gap="16px">
                          <a
                            href={TERMS_CONDITION}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={mobileDrawerFooterLinkClass}
                          >
                            <Text textStyle="text.small">
                              {t('common.Terms', 'Terms')}
                            </Text>
                          </a>
                          <a
                            href={GIT_BOOK_AUDITS}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={mobileDrawerFooterLinkClass}
                          >
                            <Text textStyle="text.small">
                              {t('common.Audits', 'Audits')}
                            </Text>
                          </a>
                        </HStack>
                        <HStack gap="12px">
                          <a
                            href={DISCORD_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <styled.img
                              src="/images/discord.svg"
                              alt="discord"
                              width={{ base: '24px' }}
                              height={{ base: '24px' }}
                            />
                          </a>
                          <a
                            href={TELEGRAM_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <styled.img
                              src="/images/telegram.svg"
                              alt="telegram"
                              width={{ base: '24px' }}
                              height={{ base: '24px' }}
                            />
                          </a>
                          <a
                            href={X_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <styled.img
                              src="/images/x.svg"
                              alt="x"
                              width={{ base: '24px' }}
                              height={{ base: '24px' }}
                            />
                          </a>
                        </HStack>
                      </Flex>
                    </Drawer.Footer>
                  </Drawer.Content>
                </Drawer.Positioner>
              </Drawer.Root>
            </HStack>

            <TopTabs
              display={{
                base: 'none',
                desktop: 'block',
              }}
              ml="28px"
            />
            <TopMoreMenu
              display={{
                base: 'none',
                desktop: 'flex',
              }}
              alignItems="center"
              ml="6px"
            />
          </Flex>

          <Flex
            alignItems="center"
            overflow="visible"
            gap={{
              desktop: '16px',
            }}
          >
            <HeaderVipButton
              display={{ base: 'none', desktop: 'inline-flex' }}
            />
            {isConnected &&
            isOneClickAllowlisted &&
            hasCheckedOneClickStatus &&
            !isOneClickEnabled ? (
              <Button
                variant="unstyled"
                className={enableOneClickButtonClass}
                onClick={openOneClickTrading}
                display={{
                  base: 'none',
                  desktop: 'flex',
                }}
                alignItems="center"
                gap="2px"
              >
                <styled.img
                  src="/images/lightning.svg"
                  alt="lightning"
                  width="20px"
                  height="20px"
                />
                {t('topNav.enable1ct', 'Enable 1CT')}
              </Button>
            ) : null}
            {children}
            <ConnectWallet
              display={{
                base: 'none',
                desktop: 'flex',
              }}
            />
          </Flex>
        </Flex>
      </Box>
      <Box
        position={{
          base: 'relative',
          desktop: 'fixed',
        }}
        top={{
          base: 'auto',
          desktop: 'layout.topNavBarHeight',
        }}
        w="full"
        zIndex="banner"
      >
        <AnnouncementBanner />
      </Box>
    </>
  );
};
